/**
 * Detection-box outline widths, sized from the box rather than the zoom level.
 *
 * The boxes sit inside the pan/zoom transform, so a width in their own px gets
 * multiplied by the current zoom — 2px paints as 20px at 10x. Dividing the
 * bounds by `--cb-zoom` (published by each zoomable view) cancels that, and
 * `cqmin` measures the box itself, so the painted thickness comes out as
 * `clamp(MIN_SCREEN_PX, BOX_SIZE_RATIO% of the box's on-screen short side,
 * the state's 1x weight)`.
 *
 * Sizing from the box matters because the same zoom level means very different
 * box sizes: a shelf shot from across the aisle has tiny facings, one shot at
 * arm's length has big ones.
 *
 * Only SVG strokes honour the sub-pixel widths this yields at deep zoom; a CSS
 * border snaps up to 1px and thickens with zoom again.
 */

/** Carries the view's live zoom scale down to its boxes. */
export const BOX_OUTLINE_ZOOM_VAR = "--cb-zoom";

/** Thinnest outline, in screen px. Below this a box stops reading. */
const MIN_SCREEN_PX = 0.75;

/** Outline thickness as a % of the box's on-screen short side. */
const BOX_SIZE_RATIO = 3;

/** Publish a view's zoom scale to the boxes under `el`. */
export const setBoxOutlineZoom = (el: HTMLElement | null, scale: number) => {
  el?.style.setProperty(BOX_OUTLINE_ZOOM_VAR, String(scale));
};

/** An outline painting at most `nominalPx` — this state's weight at 1x. */
export const boxOutlineWidth = (nominalPx: number): string => {
  const zoom = `var(${BOX_OUTLINE_ZOOM_VAR}, 1)`;
  return `clamp(calc(${MIN_SCREEN_PX}px / ${zoom}), ${BOX_SIZE_RATIO}cqmin, calc(${nominalPx}px / ${zoom}))`;
};
